import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_ANIMATIONEN } from "../makros";

export const lektionAnimationGesamtcheck: Lesson = {
  id: "gesamtcheck",
  title: "Der Gesamtcheck: sparsam und robust",
  summary: "Wie dieses Modul prüft – und ob deine Präsentation auch ohne Animationen funktioniert.",
  durationMinutes: 6,
  learningGoals: [
    "Verstehen, wie Übergänge und Animationen ohne Office.js geprüft werden",
    "Die eigene Präsentation auf sparsame Animationen prüfen",
  ],
  steps: [
    {
      id: "wie-geprueft-wird",
      title: "Wie dieses Modul prüft",
      explanation: [
        "Die Office.js-Schnittstelle, über die der Kurs sonst prüft, kennt weder " +
          "Übergänge noch Animationen – auch nicht in der neuesten Version. Das " +
          "VBA-Objektmodell von PowerPoint dagegen schon: SlideShowTransition für den " +
          "Übergang jeder Folie, TimeLine.MainSequence für ihre Animationen.",
        "Die Prüfmakros dieses Moduls lesen genau diese Angaben aus und legen sie als " +
          "Prüfbericht in der Datei ab. Das Add-in liest den Bericht und bewertet ihn. So " +
          "lässt sich prüfen, was vorher nur beschrieben werden konnte – solange PowerPoint " +
          "für Windows oder Mac läuft, denn im Browser gibt es kein VBA.",
      ],
      keyPoints: [
        "Übergänge: SlideShowTransition. Animationen: TimeLine.MainSequence.",
        "Das Prüfmakro liest nur und schreibt einen Prüfbericht in die Datei.",
        "Makros laufen nur in PowerPoint für Windows und Mac.",
      ],
    },
    {
      id: "sparsam-pruefen",
      title: "Sparsam und robust",
      explanation: [
        "Zum Abschluss der Blick aufs Ganze. Eine Präsentation, in der jede Folie animiert " +
          "ist, hat keine Animationen mehr, die etwas bedeuten. Faustregeln: höchstens jede " +
          "dritte Folie animiert, keine Folie mit mehr als sechs Effekten, und " +
          "Ausgangseffekte nur, wenn etwas wirklich verschwinden muss.",
        "Und der PDF-Test aus der ersten Lektion: Speichere die Präsentation als PDF und " +
          "blättere durch. Was dort unverständlich ist, war auf eine Animation angewiesen.",
      ],
      keyPoints: [
        "Höchstens jede dritte Folie animiert.",
        "Höchstens sechs Effekte je Folie.",
        "Der PDF-Test findet Folien, die ohne Animation nicht funktionieren.",
      ],
      task: {
        instruction:
          "Überarbeite deine Präsentation so, dass höchstens jede dritte Folie animiert ist " +
          "und keine Folie mehr als sechs Effekte hat.",
        hint: "Im Animationsbereich lassen sich Effekte markieren und mit Entf löschen.",
        macro: MAKRO_ANIMATIONEN,
        check: () =>
          pruefeMitMakro(MAKRO_ANIMATIONEN, (bericht) => {
            const folien = [...bericht.gruppe("folie").entries()];
            const animiert = folien.filter(([, folie]) => Number(folie.get("effekte") ?? 0) > 0);
            const ueberladen = folien.filter(([, folie]) => Number(folie.get("effekte") ?? 0) > 6);
            return fromCriteria(
              [
                criterion(`Mindestens drei Folien (gefunden: ${folien.length})`, folien.length >= 3),
                criterion(
                  `Höchstens jede dritte Folie animiert (${animiert.length} von ${folien.length})`,
                  animiert.length * 3 <= folien.length,
                ),
                criterion(
                  ueberladen.length === 0
                    ? "Keine Folie hat mehr als sechs Effekte"
                    : `Zu viele Effekte auf Folie ${ueberladen.map(([nr]) => nr).join(", ")}`,
                  ueberladen.length === 0,
                ),
              ],
              {
                success: "Animationen dort, wo sie etwas erklären – und nirgends sonst.",
                failure: "Die Präsentation ist noch zu stark animiert.",
              },
            );
          }),
      },
    },
  ],
};
